/**
 * Typed wrappers for the AI prompt-store custom endpoints (Epic
 * viq-backend#41). CRUD on `prompt-templates` records goes through Refine's
 * dataProvider (global resource, tenant-scope-exempt); the versioning,
 * rollout, and apply/detach surfaces are custom routes handled here.
 *
 * Backend: viq-backend/apps/superadmin-console/src/handlers/promptStore.ts.
 */
import type {
  CorePromptSummary,
  CoreVersion,
  CompanyPromptGridRow,
  CompanyPromptRow,
  PromptPipeline,
} from '@viq-admin/types';
import { superadminFetch } from './dataProvider';

/**
 * The extension seam: every prompt surface renders one unit per pipeline.
 * Finding I-7: gains the three contact-enrichment pipelines (viq-vault spec
 * 2026-09-23-contact-enrichment-agent, plan 2) alongside the original
 * photo/voice_text capture pipelines — every consumer (core-prompts list,
 * prompt-rollout list, prompt-templates create/edit, CompanyPromptsSection)
 * maps over this array, so they pick the three up automatically.
 */
export const PROMPT_PIPELINES: readonly PromptPipeline[] = [
  'photo',
  'voice_text',
  'enrichment_research',
  'enrichment_conference',
  'enrichment_capture',
];

export const PIPELINE_LABELS: Record<PromptPipeline, string> = {
  photo: 'Photo',
  voice_text: 'Voice / Text',
  enrichment_research: 'Research playbook',
  enrichment_conference: 'Conference resolver',
  enrichment_capture: 'Capture extraction',
};

/** Body caps — mirror backend CORE_BODY_MAX / APPEND_BODY_MAX. */
export const CORE_BODY_MAX = 16000;
export const APPEND_BODY_MAX = 4000;

// ── Core prompt versioning ───────────────────────────────────────────

export async function listCorePrompts(): Promise<CorePromptSummary[]> {
  const body = await superadminFetch<{ corePrompts: CorePromptSummary[] }>('/v1/superadmin/core-prompts');
  return body.corePrompts;
}

export async function listCoreVersions(pipeline: PromptPipeline): Promise<CoreVersion[]> {
  const body = await superadminFetch<{ versions: CoreVersion[] }>(`/v1/superadmin/core-prompts/${pipeline}/versions`);
  return body.versions;
}

export async function createCoreVersion(
  pipeline: PromptPipeline,
  input: { body: string; note?: string; activate?: boolean },
): Promise<CoreVersion> {
  return superadminFetch<CoreVersion>(`/v1/superadmin/core-prompts/${pipeline}/versions`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function activateCoreVersion(pipeline: PromptPipeline, versionId: string): Promise<CoreVersion> {
  return superadminFetch<CoreVersion>(
    `/v1/superadmin/core-prompts/${pipeline}/versions/${versionId}/activate`,
    { method: 'POST' },
  );
}

// ── Rollout (Prompt Rollout page) ────────────────────────────────────

export async function listCompanyGrid(pipeline: PromptPipeline): Promise<CompanyPromptGridRow[]> {
  const body = await superadminFetch<{ companies: CompanyPromptGridRow[] }>(
    `/v1/superadmin/core-prompts/${pipeline}/companies`,
  );
  return body.companies;
}

/** Repin the given companies to a version (roll forward or back). Returns count repinned. */
export async function rolloutCore(
  pipeline: PromptPipeline,
  versionId: string,
  companyIds: string[],
): Promise<number> {
  const body = await superadminFetch<{ repinned: number }>(
    `/v1/superadmin/core-prompts/${pipeline}/rollout`,
    { method: 'POST', body: JSON.stringify({ versionId, companyIds }) },
  );
  return body.repinned;
}

// ── Company detail ───────────────────────────────────────────────────

export async function getCompanyPrompts(companyId: string): Promise<CompanyPromptRow[]> {
  const body = await superadminFetch<{ prompts: CompanyPromptRow[] }>(
    `/v1/superadmin/companies/${companyId}/prompts`,
  );
  return body.prompts;
}

export async function repinCompany(
  companyId: string,
  pipeline: PromptPipeline,
  pinnedVersionId: string,
): Promise<void> {
  await superadminFetch<void>(`/v1/superadmin/companies/${companyId}/prompts/${pipeline}`, {
    method: 'PUT',
    body: JSON.stringify({ pinnedVersionId }),
  });
}

// ── Template apply / reapply / detach ────────────────────────────────

/** Apply a template's append to one or more companies. Returns count applied. */
export async function applyTemplate(templateId: string, companyIds: string[]): Promise<number> {
  const body = await superadminFetch<{ applied: number }>(
    `/v1/superadmin/prompt-templates/${templateId}/companies`,
    { method: 'POST', body: JSON.stringify({ companyIds }) },
  );
  return body.applied;
}

export async function reapplyTemplate(templateId: string): Promise<number> {
  const body = await superadminFetch<{ reapplied: number }>(
    `/v1/superadmin/prompt-templates/${templateId}/companies:reapply`,
    { method: 'POST' },
  );
  return body.reapplied;
}

export async function detachTemplate(templateId: string, companyId: string): Promise<void> {
  await superadminFetch<void>(
    `/v1/superadmin/prompt-templates/${templateId}/companies/${companyId}`,
    { method: 'DELETE' },
  );
}

export async function listTemplateCompanies(
  templateId: string,
): Promise<Array<{ companyId: string; name: string }>> {
  const body = await superadminFetch<{ companies: Array<{ companyId: string; name: string }> }>(
    `/v1/superadmin/prompt-templates/${templateId}/companies`,
  );
  return body.companies;
}
